import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import _ from "underscore";
import styles from "./prediction.module.css";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import TargetIcon from "../../../assets/icons/target.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import CircleInfoIcon from "../../../assets/icons/circle-info.svg";
import SettingsIcon from "../../../assets/icons/settings.svg";
import UpdateIcon from "../../../assets/icons/update-icon.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import { financePredictions, /* studentFeaturesTraining, */ predictionHistory, predictiveNextRun, monthlyDelinquency as fetchMonthlyDelinquency, delinquencyTrend } from "../../../services/prediction";
import { useInstitution } from "../../../context/InstitutionContext";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/loading.gif";
import api from "../../../http/api";
import { Line, Doughnut } from "react-chartjs-2";
import { usePdfExport } from "../../../hooks/usePdfExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, ChartTooltip, Legend, Filler);

const Prediction = () => {
  const { institution } = useInstitution();

  const [searchTerm1, setSearchTerm1] = useState("");
  // const [searchTerm2, setSearchTerm2] = useState("");
  const [predictionData, setPredictionData] = useState(null);
  // const [studentsData, setStudentsData] = useState(null);
  const [loading, setLoading] = useState(false);
  // const [studentsLoading, setStudentsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // const [exportingStudents, setExportingStudents] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState(null);
  // const [lastStudentsApiResponse, setLastStudentsApiResponse] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudentFactors, setSelectedStudentFactors] = useState(null);
  const [featureAverages, setFeatureAverages] = useState({});

  const [currentPage1, setCurrentPage1] = useState(1);
  // const [currentPage2, setCurrentPage2] = useState(1);
  const [itemsPerPage] = useState(15);
  const [paginationInfo, setPaginationInfo] = useState(null);
  // const [studentsPaginationInfo, setStudentsPaginationInfo] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [predictionStatus, setPredictionStatus] = useState(null);
  const [nextRunAt, setNextRunAt] = useState(null);

  const [monthlyDelinquency, setMonthlyDelinquency] = useState([]);
  const [predictionSummary, setPredictionSummary] = useState(null);

  const [trendData, setTrendData] = useState([]);
  const [trendPrediction, setTrendPrediction] = useState(null);
  const [trendLastScoredAt, setTrendLastScoredAt] = useState(null);
  const [delinquentByCourse, setDelinquentByCourse] = useState([]);
  const [predictedByCourse, setPredictedByCourse] = useState([]);
  const [courseFilter, setCourseFilter] = useState(null);

  const trendChartRef = useRef(null);
  const courseChartsRef = useRef(null);
  const topReasonRef = useRef(null);
  const { exportPdf, exportingPdf } = usePdfExport();

  const formatCurrency = (value) => {
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  };


  const formatExecutionDate = (dateString) => {
    if (!dateString) return '-';
    const normalized = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
    const date = new Date(normalized);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTopFactors = (topFactorsJson) => {
    if (!topFactorsJson) return '-';
    
    try {
      const factors = JSON.parse(topFactorsJson);
      if (!Array.isArray(factors) || factors.length === 0) return '-';
      
      // Mapear os nomes técnicos para nomes mais amigáveis
      const featureNameMap = {
        'OpenInstallmentsCount_EOM': 'Parcelas em Aberto',
        'OverdueAmount_EOM': 'Valor em Atraso',
        'BilledAmount_3m': 'Valor Faturado (3m)',
        'ReceivedAmount_3m': 'Valor Recebido (3m)',
        'LatePaymentsCount_1m': 'Pagamentos em Atraso (1m)',
        'AvgDelayDays_1m': 'Média de Atraso (1m)',
        'DaysSinceLastPayment': 'Dias desde Último Pagamento',
        'AttendanceAvg_1m': 'Frequência Média (1m)',
        'FinalGradeAvg_1m': 'Nota Final Média (1m)'
      };
      
      // Pegar os 3 principais fatores e formatá-los
      const topThreeFactors = factors.slice(0, 3).map(factor => {
        const friendlyName = featureNameMap[factor.feature] || factor.feature;
        const raw = factor.contribution ?? factor.abs_contribution ?? factor.value;
        const contribution = raw != null ? Number(raw).toFixed(2) : '-';
        return `${friendlyName} (${contribution})`;
      });
      
      return topThreeFactors.join(', ');
    } catch (error) {
      console.error('Erro ao processar topFactorsJson:', error);
      return 'Erro ao processar dados';
    }
  };

  const formatDetailedFactors = (topFactorsJson, averages = {}) => {
    if (!topFactorsJson) return [];
    
    try {
      const factors = JSON.parse(topFactorsJson);
      if (!Array.isArray(factors) || factors.length === 0) return [];
      
      // Mapear os nomes técnicos para nomes mais amigáveis (baseado na REASON_ALLOWLIST)
      const featureNameMap = {
        'AttendanceAvg_1m': 'Frequência baixa no último mês',
        'AttendanceBelowThresholdCount_1m': 'Várias aulas abaixo do limite de frequência',
        'FinalGradeAvg_1m': 'Média de notas baixa',
        'IntermediateGradeAvg_1m': 'Notas intermediárias baixas',
        'FinalExamMissingCount_1m': 'Faltas em provas finais',
        'TotalHours_1m': 'Carga horária elevada',
        'ShareEAD_1m': 'Muita carga EAD',
        'ShareNightShift_1m': 'Muitas aulas no noturno',
        'DaysSinceLastPayment': 'Muitos dias desde o último pagamento',
        'ReceivedToBilledRatio_1m': 'Recebido/Boleto do mês baixo',
        'ReceivedToBilledRatio_3m': 'Recebido/Boleto em 3m baixo',
        'ReceivedAmount_1m': 'Valor recebido no mês baixo',
        'BilledAmount_1m': 'Mensalidade do mês alta',
        'BilledAmount_3m': 'Mensalidade média em 3m alta',
        // Campos adicionais para compatibilidade
        'OpenInstallmentsCount_EOM': 'Parcelas em Aberto',
        'OverdueAmount_EOM': 'Valor em Atraso',
        'ReceivedAmount_3m': 'Valor Recebido (3 meses)',
        'LatePaymentsCount_1m': 'Pagamentos em Atraso (1 mês)',
        'AvgDelayDays_1m': 'Média de Atraso em Dias (1 mês)'
      };
      
      const formatNumber = (num) => {
        if (num == null) return '-';
        return Number(num).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      const formatInteger = (num) => {
        if (num == null) return '-';
        return Math.round(Number(num)).toLocaleString('pt-BR');
      };

      return factors.map(factor => {
        const monetaryFields = [
          'OverdueAmount_EOM',
          'BilledAmount_1m',
          'BilledAmount_3m', 
          'ReceivedAmount_1m',
          'ReceivedAmount_3m'
        ];
        
        const percentageFields = [
          'ReceivedToBilledRatio_1m',
          'ReceivedToBilledRatio_3m',
          'ShareEAD_1m',
          'ShareNightShift_1m',
          'AttendanceAvg_1m'
        ];

        const integerFields = [
          'OpenInstallmentsCount_EOM',
          'LatePaymentsCount_1m',
          'AttendanceBelowThresholdCount_1m',
          'FinalExamMissingCount_1m',
          'ClosedDisciplinesCount_1m',
          'DaysSinceLastPayment',
          'TotalHours_1m'
        ];
        
        const isMonetary = monetaryFields.includes(factor.feature);
        const isPercentage = percentageFields.includes(factor.feature);
        const isInteger = integerFields.includes(factor.feature);
        
        let formattedValue = '-';
        if (factor.value != null) {
          if (isMonetary) {
            formattedValue = `R$ ${formatNumber(factor.value)}`;
          } else if (isPercentage) {
            formattedValue = `${(Number(factor.value) * 100).toFixed(1).replace('.', ',')}%`;
          } else if (isInteger) {
            formattedValue = formatInteger(factor.value);
          } else {
            formattedValue = formatNumber(factor.value);
          }
        }

        const rawContribution = factor.contribution ?? factor.abs_contribution ?? factor.value;
        const formattedContribution = rawContribution != null
          ? `${Number(rawContribution) > 0 ? '+' : ''}${formatNumber(rawContribution)}`
          : '-';
        
        const description = factor.reason_text || featureNameMap[factor.feature] || 'Descrição não disponível';

        const avgKey = factor.feature;
        const avgRaw = averages[avgKey] ?? averages[avgKey.charAt(0).toLowerCase() + avgKey.slice(1)];
        let formattedAvg = null;
        if (avgRaw != null) {
          if (isMonetary) {
            formattedAvg = `R$ ${formatNumber(avgRaw)}`;
          } else if (isPercentage) {
            formattedAvg = `${(Number(avgRaw) * 100).toFixed(1).replace('.', ',')}%`;
          } else if (isInteger) {
            formattedAvg = formatInteger(avgRaw);
          } else {
            formattedAvg = formatNumber(avgRaw);
          }
        }

        let comparisonText = null;
        if (factor.value != null && avgRaw != null) {
          const val = Number(factor.value);
          const avg = Number(avgRaw);
          if (avg !== 0) {
            const pctDiff = ((val - avg) / Math.abs(avg)) * 100;
            const absPct = Math.abs(pctDiff).toFixed(0);
            if (pctDiff > 5) {
              comparisonText = `${absPct}% acima da média`;
            } else if (pctDiff < -5) {
              comparisonText = `${absPct}% abaixo da média`;
            } else {
              comparisonText = 'próximo da média';
            }
          }
        }
        
        return {
          feature: factor.feature,
          friendlyName: featureNameMap[factor.feature] || factor.feature,
          description: description,
          contribution: rawContribution,
          formattedContribution: formattedContribution,
          value: formattedValue,
          formattedAvg: formattedAvg,
          comparisonText: comparisonText,
          absContribution: factor.abs_contribution,
          isMonetary: isMonetary,
          isPercentage: isPercentage
        };
      });
    } catch (error) {
      console.error('Erro ao processar topFactorsJson detalhado:', error);
      return [];
    }
  };

  const handleOpenModal = (topFactorsJson, studentId) => {
    const detailedFactors = formatDetailedFactors(topFactorsJson, featureAverages);
    setSelectedStudentFactors({
      studentId,
      factors: detailedFactors
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedStudentFactors(null);
  };

  const handleOpenInfoModal = () => {
    setInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModalOpen(false);
  };



  const [generating, setGenerating] = useState(false);
  const [runningProcess, setRunningProcess] = useState(null);

  const handleGeneratePrediction = async () => {
    if (generating || runningProcess) return;

    try {
      setGenerating(true);
      toast.info("Iniciando análise preditiva completa (treinamento + scoring)...");

      const response = await api.client.post(
        'api/v1/PredictiveAnalysis/run',
        null,
        { headers: { institutionId: institution.id } }
      );

      if (response.data?.success) {
        toast.success(response.data.message || "Análise preditiva concluída!");
        setRunningProcess(null);
        fetchPredictionData(1, "");
        // fetchStudentsData(1, "");
      }
    } catch (error) {
      const data = error?.response?.data;
      toast.error(data?.message || "Erro ao executar a análise preditiva. Tente novamente.");
    } finally {
      setGenerating(false);
    }
  };

  const fetchPredictionStatus = useCallback(async () => {
    try {
      const response = await predictionHistory({
        institutionId: institution.id,
        pageNumber: 1,
        pageSize: 5
      });

      // backtest roda depois do train/score no job semanal; para explicar a tela
      // o que importa é a última execução que gera (ou não) previsões
      const history = (response?.predictionHistory?.data || []).filter(
        (h) => h.jobType !== "backtest"
      );
      if (history.length > 0) {
        const latest = history[0];
        let note = null;
        if (latest.metricsJson) {
          try {
            const metrics = JSON.parse(latest.metricsJson);
            note = metrics.note || null;
          } catch { /* ignore parse errors */ }
        }

        if (latest.status === "running" || latest.status === "pending") {
          setRunningProcess(latest);
        }

        setPredictionStatus({
          status: latest.status,
          note,
          errorMessage: latest.errorMessage,
          endedAt: latest.endedAt,
          jobType: latest.jobType,
          rowsRead: latest.rowsRead,
          rowsWritten: latest.rowsWritten,
        });
      } else {
        setPredictionStatus({ status: "never-run" });
      }
    } catch {
      setPredictionStatus(null);
    }

    const nextRun = await predictiveNextRun();
    setNextRunAt(nextRun?.nextRunAt || null);
  }, [institution.id]);

  const fetchPredictionData = useCallback((pageNumber = 1, searchTerm = "", courseName = null) => {
    const debouncedFetch = _.debounce(async () => {
      try {
        setLoading(true);
        const response = await financePredictions({
          institutionId: institution.id,
          pageNumber: pageNumber,
          pageSize: itemsPerPage,
          searchTerm: searchTerm,
          courseName: courseName || undefined
        });

        setLastApiResponse(response.financePredictions);
        setPredictionData(response.financePredictions.data);
        setPaginationInfo(response.financePredictions.pagination);

        if (response.financePredictions.featureAverages) {
          setFeatureAverages(response.financePredictions.featureAverages);
        }

        if (!response.financePredictions.data || response.financePredictions.data.length === 0) {
          fetchPredictionStatus();
        }
      } catch (error) {
        console.error("Erro ao buscar dados de predição:", error);
        fetchPredictionStatus();
      } finally {
        setLoading(false);
      }
    }, 1000);

    debouncedFetch();
  }, [institution.id, itemsPerPage, fetchPredictionStatus]);


  /* COMENTADO: tabela Base de Alunos temporariamente desativada
  const fetchStudentsData = useCallback((pageNumber = 1, searchTerm = "") => {
    const debouncedFetch = _.debounce(async () => {
      try {
        setStudentsLoading(true);
        const response = await studentFeaturesTraining({
          institutionId: institution.id,
          pageNumber: pageNumber,
          pageSize: itemsPerPage,
          searchTerm: searchTerm
        });

        console.log("Resposta da API studentFeaturesTraining:", response);

        let rawData = [];
        let pagination = null;
        
        if (response.data && response.data.studentFeaturesTraining) {
          setLastStudentsApiResponse(response.data.studentFeaturesTraining);
          rawData = response.data.studentFeaturesTraining.data;
          pagination = response.data.studentFeaturesTraining.pagination;
        } else if (response.studentFeaturesTraining) {
          setLastStudentsApiResponse(response.studentFeaturesTraining);
          rawData = response.studentFeaturesTraining.data;
          pagination = response.studentFeaturesTraining.pagination;
        } else {
          console.error("Estrutura de resposta inesperada:", response);
          setStudentsData([]);
          setStudentsPaginationInfo(null);
          return;
        }

        setStudentsData(rawData);
        setStudentsPaginationInfo(pagination);
      } catch (error) {
        console.error("Erro ao buscar dados de treinamento:", error);
      } finally {
        setStudentsLoading(false);
      }
    }, 1000);

    debouncedFetch();
  }, [institution.id, itemsPerPage]);
  */


  const fetchDelinquencyChartData = useCallback(async (forceRefresh = false) => {
    try {
      const res = await fetchMonthlyDelinquency({ institutionId: institution.id, forceRefresh });
      if (!res) return;

      const val = res.monthlyDelinquency || res;
      const rows = val.data || val.Data || [];
      if (Array.isArray(rows) && rows.length > 0) {
        setMonthlyDelinquency(rows);
      }

      const prediction = val.predictionSummary || val.PredictionSummary;
      if (prediction && (prediction.studentsScored > 0 || prediction.StudentsScored > 0)) {
        setPredictionSummary({
          studentsScored: prediction.studentsScored ?? prediction.StudentsScored ?? 0,
          predictedDelinquents: prediction.predictedDelinquents ?? prediction.PredictedDelinquents ?? 0
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados do gráfico de inadimplência:", error);
    }
  }, [institution.id]);

  const fetchDelinquencyTrend = useCallback(async (forceRefresh = false) => {
    try {
      const res = await delinquencyTrend({ institutionId: institution.id, forceRefresh });
      if (!res) return;

      const trend = res.delinquencyTrend || res.DelinquencyTrend || res;
      const rows = trend.monthlyData || trend.MonthlyData || [];
      if (Array.isArray(rows) && rows.length > 0) {
        setTrendData(rows);
      }

      const pred = trend.prediction || trend.Prediction;
      if (pred) {
        const predicted = pred.predictedDelinquents ?? pred.PredictedDelinquents ?? 0;
        const lastScored = pred.lastScoredAt ?? pred.LastScoredAt ?? null;
        setTrendPrediction(predicted);
        setTrendLastScoredAt(lastScored);
      }

      const delByCourse = trend.delinquentByCourse || trend.DelinquentByCourse || [];
      if (Array.isArray(delByCourse)) setDelinquentByCourse(delByCourse);

      const predByCourse = trend.predictedByCourse || trend.PredictedByCourse || [];
      if (Array.isArray(predByCourse)) setPredictedByCourse(predByCourse);
    } catch (error) {
      console.error("Erro ao buscar tendência de inadimplência:", error);
    }
  }, [institution.id]);

  const processedPredictionData = predictionData ? predictionData.map((item) => ({
    id: item.studentId,
    studentName: `${item.studentId}`,
    courseName: item.courseName || '-',
    horizonDays: item.horizonDays,
    probability: item.probability,
    riskBand: item.riskBand,
    refMonth: item.refMonth ? new Date(item.refMonth).toLocaleDateString('pt-BR') : '-',
    scoredAt: item.scoredAt,
    topFactors: formatTopFactors(item.topFactorsJson),
    topFactorsJson: item.topFactorsJson
  })) : [];

  const predictionHorizonDays = processedPredictionData.length > 0
    ? processedPredictionData[0].horizonDays
    : null;

  const topReasonIndicator = useMemo(() => {
    if (!predictionData || predictionData.length === 0) return null;

    const reasonNameMap = {
      'AttendanceAvg_1m': 'Frequência baixa no último mês',
      'AttendanceBelowThresholdCount_1m': 'Várias aulas abaixo do limite de frequência',
      'FinalGradeAvg_1m': 'Média de notas baixa',
      'IntermediateGradeAvg_1m': 'Notas intermediárias baixas',
      'FinalExamMissingCount_1m': 'Faltas em provas finais',
      'TotalHours_1m': 'Carga horária elevada',
      'ShareEAD_1m': 'Muita carga EAD',
      'ShareNightShift_1m': 'Muitas aulas no noturno',
      'DaysSinceLastPayment': 'Muitos dias desde o último pagamento',
      'ReceivedToBilledRatio_1m': 'Proporção recebido/cobrado baixa (1m)',
      'ReceivedToBilledRatio_3m': 'Proporção recebido/cobrado baixa (3m)',
      'ReceivedAmount_1m': 'Valor recebido no mês baixo',
      'BilledAmount_1m': 'Mensalidade do mês alta',
      'BilledAmount_3m': 'Mensalidade média em 3m alta',
      'OpenInstallmentsCount_EOM': 'Parcelas em aberto',
      'OverdueAmount_EOM': 'Valor em atraso elevado',
      'ReceivedAmount_3m': 'Valor recebido (3 meses) baixo',
      'LatePaymentsCount_1m': 'Pagamentos em atraso (1 mês)',
      'AvgDelayDays_1m': 'Média de atraso em dias elevada',
    };

    const featureCounts = {};
    let totalStudents = 0;

    predictionData.forEach(student => {
      if (!student.topFactorsJson) return;
      try {
        const factors = JSON.parse(student.topFactorsJson);
        if (!Array.isArray(factors) || factors.length === 0) return;
        totalStudents++;
        const topFeature = factors[0].feature;
        featureCounts[topFeature] = (featureCounts[topFeature] || 0) + 1;
      } catch { /* ignore */ }
    });

    if (totalStudents === 0) return null;

    const sorted = Object.entries(featureCounts).sort((a, b) => b[1] - a[1]);

    return {
      totalStudents,
      reasons: sorted.slice(0, 3).map(([feature, count]) => ({
        feature,
        friendlyName: reasonNameMap[feature] || feature,
        count,
        percentage: Math.round((count / totalStudents) * 100),
      })),
    };
  }, [predictionData]);

  /* COMENTADO: tabela Base de Alunos temporariamente desativada
  const processedStudentsData = studentsData ? studentsData.map((item) => ({
    id: item.studentId,
    studentId: item.studentId,
    refMonth: item.refMonth ? new Date(item.refMonth).toLocaleDateString('pt-BR') : '-',
    refMonthOriginal: item.refMonth,
    billedAmount_1m: item.billedAmount_1m,
    receivedAmount_1m: item.receivedAmount_1m,
    billedInstallments_1m: item.billedInstallmentsCount_1m,
    latePayments_1m: item.latePaymentsCount_1m,
    overdueAmount_EOM: item.overdueAmount_EOM,
    openInstallments_EOM: item.openInstallmentsCount_EOM,
    daysSinceLastPayment: item.daysSinceLastPayment,
    billedAmount_3m: item.billedAmount_3m,
    receivedAmount_3m: item.receivedAmount_3m,
    receivedToBilledRatio_1m: item.receivedToBilledRatio_1m,
    receivedToBilledRatio_3m: item.receivedToBilledRatio_3m,
    attendanceAvg_1m: item.attendanceAvg_1m,
    attendanceBelowThreshold_1m: item.attendanceBelowThresholdCount_1m,
    finalGradeAvg_1m: item.finalGradeAvg_1m,
    finalGradeBelowPassing_1m: item.finalGradeBelowPassingCount_1m,
    intermediateGradeAvg_1m: item.intermediateGradeAvg_1m,
    finalExamMissing_1m: item.finalExamMissingCount_1m,
    theoreticalHours_1m: item.theoreticalHours_1m,
    practicalHours_1m: item.practicalHours_1m,
    extensionHours_1m: item.extensionHours_1m,
    totalHours_1m: item.totalHours_1m,
    shareEAD_1m: item.shareEAD_1m,
    shareNightShift_1m: item.shareNightShift_1m,
    closedDisciplines_1m: item.closedDisciplinesCount_1m,
    isDelinquent: item.isDelinquentNextH
  })) : [];
  */



  // Ordenar tabela de predição por probabilidade (maior para menor)
  const currentTableData1 = processedPredictionData ? [...processedPredictionData].sort((a, b) => b.probability - a.probability) : [];
  
  /* COMENTADO: tabela Base de Alunos temporariamente desativada
  const currentTableData2 = processedStudentsData ? [...processedStudentsData].sort((a, b) => {
    const dateA = new Date(a.refMonthOriginal);
    const dateB = new Date(b.refMonthOriginal);
    return dateB - dateA;
  }) : [];
  */


  const handlePageClick1 = (page) => {
    setCurrentPage1(page);
    fetchPredictionData(page, searchTerm1, courseFilter);
  };

  /* COMENTADO: tabela Base de Alunos temporariamente desativada
  const handlePageClick2 = (page) => {
    setCurrentPage2(page);
    fetchStudentsData(page, searchTerm2);
  };
  */


  const handleSearch1 = (term) => {
    setSearchTerm1(term);
    setCurrentPage1(1);
    fetchPredictionData(1, term, courseFilter);
  };

  /* COMENTADO: tabela Base de Alunos temporariamente desativada
  const handleSearch2 = (term) => {
    setSearchTerm2(term);
    setCurrentPage2(1);
    fetchStudentsData(1, term);
  };
  */

  const handleExportCsv = async () => {
    if (!institution?.id) {
      toast.error("Instituição não encontrada");
      return;
    }

    if (!lastApiResponse?.executedQuery) {
      toast.error("Nenhuma consulta disponível para exportação");
      return;
    }

    try {
      setExporting(true);
      toast.info("Preparando exportação...");

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear - 3}-01-01`;
      const endDate = `${currentYear + 3}-12-31`;

      const exportQuery = lastApiResponse.executedQuery
        .replace(/OFFSET @Offset ROWS\s*FETCH NEXT @PageSize ROWS ONLY/gi, '');

      const exportPayload = {
        executedQuery: exportQuery,
        parameters: {
          InstitutionId: institution.id,
          StartDate: startDate,
          EndDate: endDate,
        },
        headers: {
          StudentId: "Id do Aluno",
          CourseName: "Curso",
          HorizonDays: "Dias em Atraso",
          Probability: "Probabilidade",
          RiskBand: "Risco",
          RefMonth: "Mês de Referência",
          ScoredAt: "Data de Pontuação",
          TopFactorsJson: "Motivos da Inadimplência"
        },
        fileName: "dados_predicoes_inadimplencia.csv",
        includeHeaders: true,
        delimiter: ";",
        culture: "pt-BR"
      };

      if (searchTerm1) {
        exportPayload.parameters.SearchTerm = searchTerm1;
      }

      if (courseFilter) {
        exportPayload.parameters.CourseName = courseFilter;
      }

      const response = await api.client.post(
        'api/v1/CsvExport/export-from-query',
        exportPayload,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            institutionId: institution.id
          }
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportPayload.fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");

    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!institution?.id || !predictionData || predictionData.length === 0) {
      toast.error("Nenhum dado disponível para exportação");
      return;
    }

    try {
      toast.info("Preparando PDF... Buscando todos os registros.");

      const totalRecords = paginationInfo?.totalCount || predictionData.length;
      const allDataResponse = await financePredictions({
        institutionId: institution.id,
        pageNumber: 1,
        pageSize: totalRecords,
        searchTerm: searchTerm1 || undefined,
        courseName: courseFilter || undefined,
      });

      const allData = allDataResponse?.financePredictions?.data || predictionData;

      const tableColumns = [
        "Id do Aluno", "Curso", "Horizonte (dias)",
        "Probabilidade", "Risco", "Mês de Referência",
        "Data de Pontuação", "Motivos"
      ];

      const reasonNameMap = {
        'AttendanceAvg_1m': 'Frequência baixa no último mês',
        'AttendanceBelowThresholdCount_1m': 'Várias aulas abaixo do limite de frequência',
        'FinalGradeAvg_1m': 'Média de notas baixa',
        'IntermediateGradeAvg_1m': 'Notas intermediárias baixas',
        'FinalExamMissingCount_1m': 'Faltas em provas finais',
        'TotalHours_1m': 'Carga horária elevada',
        'ShareEAD_1m': 'Muita carga EAD',
        'ShareNightShift_1m': 'Muitas aulas no noturno',
        'DaysSinceLastPayment': 'Muitos dias desde o último pagamento',
        'ReceivedToBilledRatio_1m': 'Proporção recebido/cobrado baixa (1m)',
        'ReceivedToBilledRatio_3m': 'Proporção recebido/cobrado baixa (3m)',
        'ReceivedAmount_1m': 'Valor recebido no mês baixo',
        'BilledAmount_1m': 'Mensalidade do mês alta',
        'BilledAmount_3m': 'Mensalidade média em 3m alta',
        'OpenInstallmentsCount_EOM': 'Parcelas em aberto',
        'OverdueAmount_EOM': 'Valor em atraso elevado',
        'ReceivedAmount_3m': 'Valor recebido (3 meses) baixo',
        'LatePaymentsCount_1m': 'Pagamentos em atraso (1 mês)',
        'AvgDelayDays_1m': 'Média de atraso em dias elevada',
      };

      const formatFactorsForPdf = (topFactorsJson) => {
        if (!topFactorsJson) return "-";
        try {
          const factors = JSON.parse(topFactorsJson);
          if (!Array.isArray(factors) || factors.length === 0) return "-";

          const monetaryFields = [
            'OverdueAmount_EOM', 'BilledAmount_1m', 'BilledAmount_3m',
            'ReceivedAmount_1m', 'ReceivedAmount_3m'
          ];
          const percentageFields = [
            'ReceivedToBilledRatio_1m', 'ReceivedToBilledRatio_3m',
            'ShareEAD_1m', 'ShareNightShift_1m', 'AttendanceAvg_1m'
          ];
          const integerFields = [
            'OpenInstallmentsCount_EOM', 'LatePaymentsCount_1m',
            'AttendanceBelowThresholdCount_1m', 'FinalExamMissingCount_1m',
            'ClosedDisciplinesCount_1m', 'DaysSinceLastPayment', 'TotalHours_1m'
          ];

          const fmtNum = (n) => n == null ? null : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const fmtInt = (n) => n == null ? null : Math.round(Number(n)).toLocaleString('pt-BR');

          const fmtValue = (feature, val) => {
            if (val == null) return null;
            if (monetaryFields.includes(feature)) return `R$ ${fmtNum(val)}`;
            if (percentageFields.includes(feature)) return `${(Number(val) * 100).toFixed(1).replace('.', ',')}%`;
            if (integerFields.includes(feature)) return fmtInt(val);
            return fmtNum(val);
          };

          return factors.map(f => {
            const name = reasonNameMap[f.feature] || f.feature;
            const studentVal = fmtValue(f.feature, f.value);
            const avgKey = f.feature;
            const avgRaw = featureAverages[avgKey] ?? featureAverages[avgKey.charAt(0).toLowerCase() + avgKey.slice(1)];
            const avgVal = fmtValue(f.feature, avgRaw);

            const parts = [name];
            if (studentVal) {
              parts.push(`Aluno: ${studentVal}`);
            }
            if (avgVal) {
              parts.push(`Média: ${avgVal}`);
            }
            return parts.join(' | ');
          }).join("\n");
        } catch { return "-"; }
      };

      const tableRows = allData
        .sort((a, b) => (b.probability || 0) - (a.probability || 0))
        .map(item => [
          String(item.studentId),
          item.courseName || "-",
          String(item.horizonDays ?? "-"),
          `${(item.probability * 100).toFixed(1)}%`,
          item.riskBand || "-",
          item.refMonth ? new Date(item.refMonth).toLocaleDateString("pt-BR") : "-",
          item.scoredAt ? formatExecutionDate(item.scoredAt) : "-",
          formatFactorsForPdf(item.topFactorsJson),
        ]);

      const filename = `inadimplencia_${institution.id}_${new Date().toISOString().split("T")[0]}.pdf`;
      await exportPdf([trendChartRef, courseChartsRef, topReasonRef], {
        title: "Análise Preditiva de Inadimplência",
        filename,
        tableData: { columns: tableColumns, rows: tableRows },
        orientation: "landscape",
      });
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar PDF:", error);
      toast.error("Erro ao exportar PDF. Tente novamente.");
    }
  };

  /* COMENTADO: tabela Base de Alunos temporariamente desativada
  const handleExportStudentsCsv = async () => {
    if (!institution?.id) {
      toast.error("Instituição não encontrada");
      return;
    }

    if (!lastStudentsApiResponse?.executedQuery) {
      toast.error("Nenhuma consulta disponível para exportação");
      return;
    }

    try {
      setExportingStudents(true);
      toast.info("Preparando exportação...");

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear - 3}-01-01`;
      const endDate = `${currentYear + 3}-12-31`;

      const exportQuery = lastStudentsApiResponse.executedQuery
        .replace(/OFFSET @Offset ROWS\\s*FETCH NEXT @PageSize ROWS ONLY/gi, '');

      const exportPayload = {
        executedQuery: exportQuery,
        parameters: {
          InstitutionId: institution.id,
          StartDate: startDate,
          EndDate: endDate,
        },
        headers: {
          StudentId: "ID do Aluno",
          RefMonth: "Mês de Referência",
          BilledAmount_1m: "Valor Faturado (1m)",
          ReceivedAmount_1m: "Valor Recebido (1m)",
          BilledInstallmentsCount_1m: "Parcelas Faturadas (1m)",
          LatePaymentsCount_1m: "Parcelas em Atraso (1m)",
          AvgDelayDays_1m: "Média de Atraso (dias)",
          OverdueAmount_EOM: "Valor em Atraso",
          OpenInstallmentsCount_EOM: "Parcelas Abertas",
          DaysSinceLastPayment: "Dias desde Último Pagamento",
          BilledAmount_3m: "Valor Faturado (3m)",
          ReceivedAmount_3m: "Valor Recebido (3m)",
          ReceivedToBilledRatio_1m: "Taxa Recebido/Faturado (1m)",
          ReceivedToBilledRatio_3m: "Taxa Recebido/Faturado (3m)",
          AttendanceAvg_1m: "Frequência (%)",
          AttendanceBelowThresholdCount_1m: "Frequência Abaixo do Limite",
          FinalGradeAvg_1m: "Nota Final Média",
          FinalGradeBelowPassingCount_1m: "Notas Abaixo da Média",
          IntermediateGradeAvg_1m: "Nota Intermediária Média",
          FinalExamMissingCount_1m: "Provas Finais Perdidas",
          TheoreticalHours_1m: "Horas Teóricas",
          PracticalHours_1m: "Horas Práticas",
          ExtensionHours_1m: "Horas de Extensão",
          TotalHours_1m: "Horas Totais",
          ShareEAD_1m: "Percentual EAD",
          ShareNightShift_1m: "Percentual Noturno",
          ClosedDisciplinesCount_1m: "Disciplinas Fechadas",
          IsDelinquentNextH: "Inadimplente"
        },
        fileName: "base_alunos_analise_predicao.csv",
        includeHeaders: true,
        delimiter: ";",
        culture: "pt-BR"
      };

      if (searchTerm2) {
        exportPayload.parameters.SearchTerm = searchTerm2;
      }

      const response = await api.client.post(
        'api/v1/CsvExport/export-from-query',
        exportPayload,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            institutionId: institution.id
          }
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportPayload.fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");

    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExportingStudents(false);
    }
  };
  */


  const getEmptyStateInfo = () => {
    if (!predictionStatus) {
      return {
        title: "Nenhuma previsão de inadimplência encontrada",
        description: "Não há dados de predição disponíveis no momento.",
        type: "default"
      };
    }

    if (predictionStatus.status === "never-run") {
      return {
        title: "Análise preditiva ainda não foi executada",
        description: "A análise preditiva ainda não foi executada para esta instituição. Você pode gerar a primeira previsão agora ou aguardar a execução automática semanal.",
        type: "info"
      };
    }

    if (predictionStatus.status === "failed") {
      return {
        title: "A última análise preditiva falhou",
        description: predictionStatus.errorMessage
          ? `Erro: ${predictionStatus.errorMessage.substring(0, 200)}`
          : "Ocorreu um erro durante a execução. A equipe técnica foi notificada.",
        type: "error"
      };
    }

    if (predictionStatus.note === "no-data") {
      return {
        title: "Sem dados para gerar previsões",
        description: "Não foram encontrados dados financeiros e acadêmicos suficientes para esta instituição. Verifique se os arquivos de Faturamento, Acadêmico e Turmas foram importados corretamente na tela de Importar Dados.",
        type: "warning"
      };
    }

    if (predictionStatus.note === "no-data-after-time-filter") {
      return {
        title: "Dados insuficientes no período analisado",
        description: "Existem dados importados, mas não há meses com cobertura de pagamentos completa para treinar o modelo. Importe um Faturamento mais atualizado, incluindo as datas de recebimento das parcelas pagas.",
        type: "warning"
      };
    }

    if (predictionStatus.note === "no-positives") {
      return {
        title: "Histórico sem casos de inadimplência",
        description: "No período coberto pelos dados importados, nenhum aluno se tornou inadimplente — o modelo não tem exemplos para aprender. Verifique se o Faturamento importado inclui também as parcelas em atraso e as datas de recebimento.",
        type: "warning"
      };
    }

    if (predictionStatus.note === "no-features-adimplentes") {
      return {
        title: "Nenhum aluno adimplente encontrado para o mês analisado",
        description: "A análise preditiva só gera previsões para alunos que estão em dia. Não foram encontrados alunos adimplentes no mês de referência. Isso pode significar que os dados do mês ainda não foram importados ou que todos os alunos possuem pendências financeiras.",
        type: "warning"
      };
    }

    // Execução manual (/run) concluída sem encontrar dados: não gera note no histórico
    if (predictionStatus.status === "succeeded" && !predictionStatus.rowsRead) {
      return {
        title: "A última execução não encontrou dados",
        description: "A análise foi executada, mas não havia dados financeiros e acadêmicos suficientes para gerar previsões. Verifique se os arquivos de Faturamento, Acadêmico e Turmas foram importados corretamente na tela de Importar Dados.",
        type: "warning"
      };
    }

    if (predictionStatus.status === "succeeded" && predictionStatus.rowsWritten === 0) {
      return {
        title: "Nenhum aluno em risco identificado",
        description: "A última análise foi concluída com sucesso e nenhum aluno em dia apresentou risco médio ou alto de inadimplência no horizonte analisado.",
        type: "info"
      };
    }

    // Última análise gerou previsões, mas o lote passou da janela de exibição (14 dias)
    if (predictionStatus.status === "succeeded" && predictionStatus.rowsWritten > 0) {
      return {
        title: "Previsões desatualizadas",
        description: "A última análise gerou previsões, mas elas têm mais de 14 dias e não são mais exibidas. Execute uma nova análise agora ou aguarde a próxima execução automática.",
        type: "warning"
      };
    }

    return {
      title: "Nenhuma previsão de inadimplência encontrada",
      description: "Não há dados de predição disponíveis no momento.",
      type: "default"
    };
  };

  useEffect(() => {
    if (institution?.id) {
      // fetchStudentsData(1, "");
      fetchPredictionData(1, "");
      fetchDelinquencyChartData();
      fetchDelinquencyTrend();
    }
  }, [institution?.id, fetchPredictionData, fetchDelinquencyChartData, fetchDelinquencyTrend]);

  // Desabilitar scroll quando a tela de informações estiver aberta
  useEffect(() => {
    if (infoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup quando o componente desmontar
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [infoModalOpen]);


  return (
    <div className={styles.predictionContent}>
      {!infoModalOpen && (
        <>
          {/* Banner de processo em execução */}
          {runningProcess && (
            <div className={styles.runningBanner}>
              <div className={styles.runningBannerContent}>
                <img src={loadingGif} alt="Processando" className={styles.runningBannerSpinner} />
                <div className={styles.runningBannerText}>
                  <strong>Análise preditiva em execução</strong>
                  <span>
                    O processo de treinamento e scoring está sendo executado e deve finalizar nos próximos minutos.
                    Esta página será atualizada automaticamente ao concluir.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Gráfico de Evolução de Inadimplência (últimos 12 meses + previsão) */}
          {(() => {
            if (!trendLastScoredAt) return null;
            const scoredDate = new Date(
              trendLastScoredAt.endsWith('Z') || trendLastScoredAt.includes('+')
                ? trendLastScoredAt
                : trendLastScoredAt + 'Z'
            );
            const daysAgo = (Date.now() - scoredDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysAgo > 15) return null;
            if (trendData.length === 0 && trendPrediction == null) return null;

            const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
            const now = new Date();
            const nextMonthIdx = (now.getMonth() + 1) % 12;
            const nextYearSuffix = now.getMonth() === 11
              ? String(now.getFullYear() + 1).slice(-2)
              : String(now.getFullYear()).slice(-2);
            const predictionLabel = `${monthNames[nextMonthIdx]}/${nextYearSuffix}`;

            const historicalLabels = trendData.map(m => m.month || m.Month);
            const historicalCounts = trendData.map(m => m.delinquentStudents ?? m.DelinquentStudents ?? 0);

            const lastCount = historicalCounts.length > 0
              ? historicalCounts[historicalCounts.length - 1]
              : 0;
            const hasPrediction = trendPrediction != null && trendPrediction > 0;

            const allLabels = [...historicalLabels, ...(hasPrediction ? [predictionLabel] : [])];
            const realLine = [...historicalCounts, ...(hasPrediction ? [null] : [])];
            const predictionLine = hasPrediction
              ? [...Array(Math.max(0, historicalCounts.length - 1)).fill(null), lastCount, trendPrediction]
              : [];

            return (
              <div className={styles.tableContainer} ref={trendChartRef}>
                <HeaderTitle title="Evolução de alunos inadimplentes" icon={TargetIcon} />
                <span className={styles.chartSubtitle}>Últimos 12 meses + projeção dos próximos 30 dias</span>
                <div className={styles.delinquencyChartWrapper}>
                  <Line
                    data={{
                      labels: allLabels,
                      datasets: [
                        {
                          label: 'Alunos inadimplentes',
                          data: realLine,
                          borderColor: '#0962AD',
                          backgroundColor: 'rgba(9,98,173,0.08)',
                          borderWidth: 2.5,
                          tension: 0.3,
                          pointRadius: 5,
                          pointHoverRadius: 7,
                          pointBackgroundColor: '#0962AD',
                          pointBorderColor: '#fff',
                          fill: true,
                        },
                        ...(predictionLine.length > 0 ? [{
                          label: 'Previsão (30 dias)',
                          data: predictionLine,
                          borderColor: '#f59e0b',
                          backgroundColor: 'rgba(245,158,11,0.08)',
                          borderWidth: 2.5,
                          borderDash: [6, 4],
                          tension: 0.3,
                          pointRadius: 6,
                          pointHoverRadius: 8,
                          pointBackgroundColor: '#f59e0b',
                          pointBorderColor: '#fff',
                          pointStyle: 'rectRot',
                          fill: false,
                        }] : []),
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top',
                          align: 'end',
                          labels: { usePointStyle: true, padding: 16, font: { size: 12 } },
                        },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              if (ctx.parsed.y == null) return null;
                              return `${ctx.dataset.label}: ${ctx.parsed.y} aluno${ctx.parsed.y !== 1 ? 's' : ''}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                          ticks: { color: '#64748b', font: { size: 12 } },
                        },
                        y: {
                          type: 'linear',
                          beginAtZero: true,
                          title: { display: true, text: 'Quantidade de alunos', color: '#0962AD', font: { size: 12, weight: 600 } },
                          ticks: {
                            color: '#64748b',
                            precision: 0,
                            callback: (v) => Number.isInteger(v) ? v : null,
                          },
                          grid: { color: '#f1f5f9' },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            );
          })()}

          {/* Gráficos de pizza: inadimplentes por curso */}
          {(() => {
            if (!trendLastScoredAt) return null;
            const scoredDate = new Date(
              trendLastScoredAt.endsWith('Z') || trendLastScoredAt.includes('+')
                ? trendLastScoredAt
                : trendLastScoredAt + 'Z'
            );
            const daysAgo = (Date.now() - scoredDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysAgo > 15) return null;

            const hasDelinquent = delinquentByCourse.length > 0;
            const hasPredicted = predictedByCourse.length > 0;
            if (!hasDelinquent && !hasPredicted) return null;

            const courseColors = [
              '#0962AD', '#DA5252', '#f59e0b', '#10b981', '#8b5cf6',
              '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
              '#e11d48', '#84cc16', '#a855f7', '#0ea5e9', '#eab308',
            ];

            const buildPieData = (items) => {
              const labels = items.map(i => i.courseName || i.CourseName || 'N/A');
              const counts = items.map(i => i.studentCount ?? i.StudentCount ?? 0);
              const bgColors = items.map((_, idx) => courseColors[idx % courseColors.length]);
              return {
                labels,
                datasets: [{
                  data: counts,
                  backgroundColor: bgColors,
                  borderColor: '#ffffff',
                  borderWidth: 2,
                }],
              };
            };

            const buildPieOptions = (items) => ({
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    usePointStyle: true,
                    boxHeight: 6,
                    color: '#64748b',
                    font: { size: 12 },
                    padding: 12,
                    generateLabels: (chart) => {
                      const data = chart.data;
                      const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                      return data.labels.map((label, i) => {
                        const value = data.datasets[0].data[i];
                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                        return {
                          text: `${label} (${pct}%)`,
                          fillStyle: data.datasets[0].backgroundColor[i],
                          strokeStyle: data.datasets[0].borderColor,
                          lineWidth: data.datasets[0].borderWidth,
                          pointStyle: 'circle',
                          hidden: !chart.getDataVisibility(i),
                          index: i,
                        };
                      });
                    },
                  },
                },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                      const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0';
                      return `${ctx.label}: ${ctx.parsed} aluno${ctx.parsed !== 1 ? 's' : ''} (${pct}%)`;
                    },
                  },
                },
              },
            });

            return (
              <div className={styles.courseChartsRow} ref={courseChartsRef}>
                {hasDelinquent && (
                  <div className={styles.tableContainer}>
                    <HeaderTitle title="Inadimplentes por curso" icon={TargetIcon} />
                    <span className={styles.chartSubtitle}>Alunos com parcelas vencidas nos últimos 12 meses</span>
                    <div className={styles.courseChartWrapper}>
                      <Doughnut data={buildPieData(delinquentByCourse)} options={buildPieOptions(delinquentByCourse)} />
                    </div>
                  </div>
                )}
                {hasPredicted && (
                  <div className={styles.tableContainer}>
                    <HeaderTitle title="Possíveis inadimplentes por curso" icon={TargetIcon} />
                    <span className={styles.chartSubtitle}>Clique em um curso para filtrar a tabela de análise preditiva</span>
                    <div className={styles.courseChartWrapper}>
                      <Doughnut
                        data={buildPieData(predictedByCourse)}
                        options={{
                          ...buildPieOptions(predictedByCourse),
                          onClick: (_event, elements) => {
                            if (!elements || elements.length === 0) return;
                            const idx = elements[0].index;
                            const clicked = predictedByCourse[idx];
                            const name = clicked?.courseName || clicked?.CourseName || null;
                            if (name && name === courseFilter) {
                              setCourseFilter(null);
                              setCurrentPage1(1);
                              fetchPredictionData(1, searchTerm1, null);
                            } else if (name) {
                              setCourseFilter(name);
                              setCurrentPage1(1);
                              fetchPredictionData(1, searchTerm1, name);
                            }
                          },
                          onHover: (event, elements) => {
                            event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Indicador: principal motivo de possível inadimplência */}
          {topReasonIndicator && (
            <div className={styles.tableContainer} ref={topReasonRef}>
              <HeaderTitle title="Principal motivo de possível inadimplência" icon={TargetIcon} />
              <span className={styles.chartSubtitle}>
                Fator mais frequente como causa principal entre os alunos com risco de inadimplência
              </span>
              <div className={styles.topReasonList}>
                {topReasonIndicator.reasons.map((reason, index) => (
                  <div
                    key={reason.feature}
                    className={`${styles.topReasonItem} ${index === 0 ? styles.topReasonItemPrimary : ''}`}
                  >
                    <div className={`${styles.topReasonRank} ${index === 0 ? styles.topReasonRankPrimary : ''}`}>
                      {index + 1}º
                    </div>
                    <div className={styles.topReasonInfo}>
                      <span className={styles.topReasonName}>{reason.friendlyName}</span>
                      <div className={styles.topReasonBar}>
                        <div
                          className={`${styles.topReasonBarFill} ${index === 0 ? styles.topReasonBarFillPrimary : ''}`}
                          style={{ width: `${reason.percentage}%` }}
                        />
                      </div>
                      <span className={styles.topReasonCount}>
                        Fator principal em <strong>{reason.count}</strong> de{' '}
                        <strong>{topReasonIndicator.totalStudents}</strong> alunos ({reason.percentage}%)
                      </span>
                    </div>
                    <div className={`${styles.topReasonPercentage} ${index === 0 ? styles.topReasonPercentagePrimary : ''}`}>
                      {reason.percentage}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Primeira Tabela - Análise Preditiva de Inadimplência */}
          <div className={styles.tableContainer}>
            <div className={styles.headerTitleWithActions}>
              <div className={styles.headerTitleGroup}>
                <HeaderTitle title="Análise Preditiva de Inadimplência" icon={TargetIcon} />
                <button 
                  className={styles.infoIconButton}
                  onClick={handleOpenInfoModal}
                  title="Como funciona a análise preditiva?"
                >
                  <img src={CircleInfoIcon} alt="Informações" className={styles.infoIcon} />
                </button>
                {courseFilter && (
                  <span className={styles.courseFilterBadge}>
                    Curso: {courseFilter}
                    <button
                      className={styles.courseFilterClear}
                      onClick={() => {
                        setCourseFilter(null);
                        setCurrentPage1(1);
                        fetchPredictionData(1, searchTerm1, null);
                      }}
                      title="Remover filtro"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
              {predictionData && predictionData.length > 0 && !loading && (
                <div className={styles.headerRight}>
                  <div className={styles.searchWrapper}>
                    <img src={SearchIcon} alt="Pesquisar" className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Pesquisar..."
                      value={searchTerm1}
                      onChange={(e) => handleSearch1(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>
                  <button
                    className={styles.updateButton}
                    onClick={() => {
                      fetchPredictionData(currentPage1, searchTerm1, courseFilter);
                      // Limpa o cache dos indicadores e recarrega com dados frescos.
                      fetchDelinquencyChartData(true);
                      fetchDelinquencyTrend(true);
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <img src={loadingGif} alt="Carregando" className={styles.loadingIcon} />
                    ) : (
                      <img src={UpdateIcon} alt="Atualizar" className={styles.updateButtonIcon} />
                    )}
                    {loading ? "Atualizando..." : "Atualizar Previsão"}
                  </button>
                  <button
                    className={styles.exportButton}
                    onClick={handleExportCsv}
                    disabled={exporting}
                    title="Exportar CSV"
                  >
                    {exporting ? (
                      <img src={loadingGif} alt="Exportando..." className={styles.exportIcon} />
                    ) : (
                      <img src={FileExportIcon} alt="Exportar" className={styles.exportIcon} />
                    )}
                    {exporting ? "Exportando..." : "Exportar CSV"}
                  </button>
                  <button
                    className={styles.exportButton}
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    title="Exportar PDF"
                  >
                    {exportingPdf ? (
                      <img src={loadingGif} alt="Exportando..." className={styles.exportIcon} />
                    ) : (
                      <img src={FileExportIcon} alt="Exportar" className={styles.exportIcon} />
                    )}
                    {exportingPdf ? "Exportando..." : "Exportar PDF"}
                  </button>
                </div>
              )}
            </div>

        {(!predictionData || predictionData.length === 0) && !loading ? (() => {
          const emptyInfo = getEmptyStateInfo();
          return (
            <div className={`${styles.emptyState} ${styles[`emptyState_${emptyInfo.type}`] || ''}`}>
              <div className={styles.emptyStateIcon}>
                <img src={CircleInfoIcon} alt="Informação" />
              </div>
              <div className={styles.emptyStateContent}>
                <h2 className={styles.emptyStateTitle}>
                  {emptyInfo.title}
                </h2>
                <p className={styles.emptyStateDescription}>
                  {emptyInfo.description}
                </p>
                {(predictionStatus?.endedAt || nextRunAt) && (
                  <div className={styles.emptyStateSchedule}>
                    {predictionStatus?.endedAt && (
                      <span>Última execução: <strong>{formatExecutionDate(predictionStatus.endedAt)}</strong></span>
                    )}
                    {nextRunAt && (
                      <span>Próxima execução automática: <strong>{formatExecutionDate(nextRunAt)}</strong></span>
                    )}
                  </div>
                )}
                {emptyInfo.type === "warning" && (
                  <a href="/app/importar-dados" className={styles.importDataLink}>
                    Ir para Importar Dados
                  </a>
                )}
                <button
                  className={styles.generateButton}
                  onClick={handleGeneratePrediction}
                  disabled={generating || !!runningProcess}
                >
                  {generating ? (
                    <img src={loadingGif} alt="Gerando..." className={styles.generateButtonIcon} />
                  ) : (
                    <img src={SettingsIcon} alt="Configurações" className={styles.generateButtonIcon} />
                  )}
                  {generating ? "Executando análise..." : runningProcess ? "Processo em execução..." : "Gerar previsão de inadimplência"}
                </button>
              </div>
            </div>
          );
        })() : (
          <div>
            {predictionHorizonDays && (
              <div className={styles.horizonBanner}>
                <div className={styles.horizonBannerIcon}>
                  <img src={TargetIcon} alt="Horizonte" />
                </div>
                <div className={styles.horizonBannerText}>
                  <strong>Horizonte de previsão: {predictionHorizonDays} dias</strong>
                  <span>
                    Os alunos listados abaixo estão atualmente em dia, mas apresentam risco de se tornarem
                    inadimplentes nos próximos <strong>{predictionHorizonDays} dias</strong> com base nos dados analisados.
                  </span>
                </div>
              </div>
            )}

            <table className={styles.predictionTable}>
              <thead>
                <tr>
                  <th>Id do Aluno</th>
                  <th>Curso</th>
                  <th>Horizonte (dias)</th>
                  <th>Probabilidade</th>
                  <th>Risco</th>
                  <th>Mês de Referência</th>
                  <th>Data de Pontuação</th>
                  <th>Motivos da Inadimplência</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                      <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                      <p>Carregando dados de predição...</p>
                    </td>
                  </tr>
                ) : currentTableData1.length > 0 ? (
                  currentTableData1.map((student, idx) => (
                    <tr key={`${student.id}-${idx}`}>
                      <td>{student.studentName}</td>
                      <td>{student.courseName}</td>
                      <td>{student.horizonDays}</td>
                      <td>{(student.probability * 100).toFixed(1)}%</td>
                      <td>
                        <span className={`${styles.riskBand} ${styles[`risk${student.riskBand}`]}`}>
                          {student.riskBand}
                        </span>
                      </td>
                      <td>{student.refMonth}</td>
                      <td>{student.scoredAt ? formatExecutionDate(student.scoredAt) : '-'}</td>
                      <td>
                        {student.topFactorsJson ? (
                          <button
                            className={styles.factorsButton}
                            onClick={() => handleOpenModal(student.topFactorsJson, student.studentName)}
                            title="Ver detalhes dos motivos de inadimplência"
                          >
                            <img src={InfoIcon} alt="Ver detalhes" className={styles.factorsIcon} />
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Nenhum dado de predição encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Paginação da primeira tabela */}
            {predictionData && predictionData.length > 0 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => handlePageClick1(currentPage1 - 1)}
                  disabled={!paginationInfo?.hasPreviousPage || currentPage1 <= 1}
                  className={styles.paginationButton}
                >
                  Anterior
                </button>

                {paginationInfo?.totalPages > 1 && (() => {
                  const totalPages = paginationInfo.totalPages;
                  const currentPage = currentPage1;
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pages = [];

                  // Primeira página
                  if (startPage > 1) {
                    pages.push(
                      <button key={1} onClick={() => handlePageClick1(1)}>
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1">...</span>);
                    }
                  }

                  // Páginas do meio
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={i === currentPage ? styles.activePage : ""}
                        onClick={() => handlePageClick1(i)}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Última página
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="ellipsis2">...</span>);
                    }
                    pages.push(
                      <button key={totalPages} onClick={() => handlePageClick1(totalPages)}>
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}

                <button
                  onClick={() => handlePageClick1(currentPage1 + 1)}
                  disabled={!paginationInfo?.hasNextPage || currentPage1 >= (paginationInfo?.totalPages || 1)}
                  className={styles.paginationButton}
                >
                  Próximo
                </button>

                <span className={styles.paginationInfo}>
                  Página {paginationInfo?.pageNumber || currentPage1} de {paginationInfo?.totalPages || 1}
                  ({paginationInfo?.totalCount || predictionData?.length || 0} itens)
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* COMENTADO: tabela Base de Alunos temporariamente desativada
      <div className={styles.tableContainer}>
        <div className={styles.headerTitleWithActions}>
          <HeaderTitle title="Base de Alunos • Análise Preditiva de Inadimplência" icon={TargetIcon} />
          <div className={styles.headerRight}>
            <div className={styles.searchWrapper}>
              <img src={SearchIcon} alt="Pesquisar" className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm2}
                onChange={(e) => handleSearch2(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <button
              className={styles.exportButton}
              onClick={handleExportStudentsCsv}
              disabled={exportingStudents}
              title="Exportar CSV"
            >
              {exportingStudents ? (
                <img src={loadingGif} alt="Exportando..." className={styles.exportIcon} />
              ) : (
                <img src={FileExportIcon} alt="Exportar" className={styles.exportIcon} />
              )}
              {exportingStudents ? "Exportando..." : "Exportar CSV"}
            </button>
          </div>
        </div>

        <table className={styles.predictionTable}>
          <thead>
            <tr>
              <th>ID do Aluno</th>
              <th>Mês de Referência</th>
              <th>Valor Faturado (1m)</th>
              <th>Valor Recebido (1m)</th>
              <th>Valor em Atraso</th>
              <th>Parcelas Abertas</th>
              <th>Frequência (%)</th>
              <th>Nota Final</th>
              <th>Horas Totais (1m)</th>
              <th>Inadimplente</th>
            </tr>
          </thead>
          <tbody>
            {studentsLoading ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                  <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                  <p>Carregando dados dos alunos...</p>
                </td>
              </tr>
            ) : currentTableData2.length > 0 ? (
              currentTableData2.map((student) => (
                <tr key={student.id}>
                  <td>{student.studentId}</td>
                  <td>{student.refMonth}</td>
                  <td>{formatCurrency(student.billedAmount_1m)}</td>
                  <td>{formatCurrency(student.receivedAmount_1m)}</td>
                  <td className={styles.valueCell}>{formatCurrency(student.overdueAmount_EOM)}</td>
                  <td>{student.openInstallments_EOM}</td>
                  <td>{student.attendanceAvg_1m}%</td>
                  <td>{student.finalGradeAvg_1m}</td>
                  <td>{student.totalHours_1m}h</td>
                  <td>{student.isDelinquent ? "Sim" : "Não"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  Nenhum dado de aluno encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {studentsPaginationInfo && studentsPaginationInfo.totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              onClick={() => handlePageClick2(currentPage2 - 1)}
              disabled={!studentsPaginationInfo.hasPreviousPage}
              className={styles.paginationButton}
            >
              Anterior
            </button>

            {Array.from({ length: Math.min(5, studentsPaginationInfo.totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  className={page === currentPage2 ? styles.activePage : ""}
                  onClick={() => handlePageClick2(page)}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageClick2(currentPage2 + 1)}
              disabled={!studentsPaginationInfo.hasNextPage}
              className={styles.paginationButton}
            >
              Próximo
            </button>

            <span className={styles.paginationInfo}>
              Página {studentsPaginationInfo.pageNumber} de {studentsPaginationInfo.totalPages}
              ({studentsPaginationInfo.totalCount} itens)
            </span>
          </div>
        )}
      </div>
      */}

        </>
      )}

      {/* Tela de Informações sobre a Análise Preditiva */}
      {infoModalOpen && (
        <div className={styles.fullScreenOverlay}>
          <div className={styles.fullScreenContent}>
            <div className={styles.fullScreenHeader}>
              <h2 className={styles.fullScreenTitle}>
                Como funciona a Análise Preditiva de Inadimplência?
              </h2>
              <button className={styles.fullScreenCloseButton} onClick={handleCloseInfoModal}>
                ×
              </button>
            </div>
            
            <div className={styles.fullScreenBody}>
              <div className={styles.infoSection}>
                <p className={styles.infoDescription}>
                  Nossa plataforma utiliza algoritmos de <strong>Machine Learning</strong> para analisar padrões históricos 
                  de comportamento acadêmico e financeiro dos alunos. Com base nesses dados, o sistema identifica 
                  estudantes que, embora estejam atualmente em dia com suas mensalidades, apresentam <strong>maior 
                  probabilidade de se tornarem inadimplentes</strong> nos próximos meses.
                </p>
                <p className={styles.infoDescription}>
                  Essa análise permite que a instituição atue de forma <strong>proativa</strong>, oferecendo suporte 
                  e acompanhamento personalizado aos alunos em situação de risco, prevenindo a inadimplência antes 
                  que ela ocorra.
                </p>
              </div>

              <div className={styles.howItWorksSection}>
                <h3 className={styles.howItWorksTitle}>Como o Algoritmo Funciona?</h3>
                
                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>1. Importação de Dados</h4>
                  <p className={styles.stepDescription}>
                    Você só precisa importar os arquivos da instituição na tela de <a href="/app/importar-dados" className={styles.infoLink}>Importar Dados</a> — principalmente <strong className={styles.highlightFile}>Faturamento</strong>, <strong className={styles.highlightFile}>Acadêmico</strong>, <strong className={styles.highlightFile}>Turmas</strong> e <strong className={styles.highlightFile}>Alunos</strong>. A partir daí, a plataforma faz todo o resto de forma automática.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>2. Validação e Treinamento</h4>
                  <p className={styles.stepDescription}>
                    Primeiro, a plataforma valida e organiza os dados e verifica se já existe um modelo atualizado para a instituição. Se não houver, treina um novo com o histórico disponível e o guarda para as próximas análises, sem que você precise ajustar nada manualmente.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>3. Cálculo de Risco</h4>
                  <p className={styles.stepDescription}>
                    Com os dados carregados, a plataforma calcula, para o mês que você estiver visualizando, a chance de cada aluno que está em dia atrasar um pagamento no período de previsão (por exemplo, 30 dias). Essa chance aparece em três faixas de risco — <strong>baixo</strong>, <strong>médio</strong> e <strong>alto</strong> — para facilitar a priorização: alto risco pede contato proativo e ações rápidas; médio risco requer atenção; baixo risco segue no acompanhamento de rotina.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>4. Motivos da Classificação</h4>
                  <p className={styles.stepDescription}>
                    Para cada aluno, também exibimos os principais motivos da classificação, em linguagem direta e com números quando fizer sentido. Exemplos: frequência baixa nas aulas, muitos dias desde o último pagamento, valor cobrado alto em relação ao que foi recebido ou notas abaixo do esperado. Isso dá contexto para a equipe escolher a melhor abordagem.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>5. Histórico e Auditoria</h4>
                  <p className={styles.stepDescription}>
                    Tudo fica salvo no sistema e pode ser consultado ou exportado. Além disso, cada execução é registrada com data, horário e resultado, permitindo acompanhar o histórico e auditar o processo sempre que necessário. Em resumo: <strong>você mantém as importações em dia; a plataforma cuida das previsões e da organização das informações.</strong>
                  </p>
                </div>

                <div className={styles.privacyNote}>
                  <div className={styles.privacyIcon}>🔒</div>
                  <div className={styles.privacyContent}>
                    <h5 className={styles.privacyTitle}>Privacidade e Segurança</h5>
                    <p className={styles.privacyText}>
                      Todos os dados da sua instituição são <strong>protegidos e mantidos em sigilo absoluto</strong>. As informações não são compartilhadas com outras instituições e permanecem exclusivas para uso interno da sua organização.
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.variablesSection}>
                <h3 className={styles.variablesTitle}>Variáveis Utilizadas no Treinamento</h3>
                <p className={styles.variablesIntro}>
                  O modelo de predição analisa diversos indicadores agrupados em três categorias principais:
                </p>

                <div className={styles.variableCategory}>
                  <h4 className={styles.variableCategoryTitle}>
                    💰 Histórico e Fluxo de Pagamentos
                  </h4>
                  <ul className={styles.variableList}>
                    <li>Muitas parcelas em atraso recente</li>
                    <li>Atrasos crescentes</li>
                    <li>Valor em aberto no fim do mês</li>
                    <li>Mais parcelas abertas que o normal</li>
                    <li>Recebimentos fracos vs. cobranças</li>
                    <li>Boleto do mês passado alto sem contrapartida</li>
                    <li>Muito tempo sem pagar</li>
                  </ul>
                </div>

                <div className={styles.variableCategory}>
                  <h4 className={styles.variableCategoryTitle}>
                    📚 Risco Acadêmico que Antecipa Evasão/Atraso
                  </h4>
                  <ul className={styles.variableList}>
                    <li>Frequência baixa</li>
                    <li>Desempenho fraco (Notas e médias finais em disciplina abaixo da média)</li>
                    <li>Ausência em provas</li>
                  </ul>
                </div>

                <div className={styles.variableCategory}>
                  <h4 className={styles.variableCategoryTitle}>
                    🎓 Características da Turma/Curso
                  </h4>
                  <ul className={styles.variableList}>
                    <li>Carga horária puxada ou desbalanceada</li>
                    <li>Perfil da oferta: <strong>Proporção de aulas EAD</strong> ou <strong>Proporção de aulas Noturno elevada</strong> podem aumentar risco</li>
                    <li>Dinâmica acadêmica: <strong>Disciplinas Encerradas nos últimos meses</strong> pode sinalizar instabilidade</li>
                  </ul>
                </div>
              </div>

              <div className={styles.faqSection}>
                <h3 className={styles.faqTitle}>Perguntas Frequentes</h3>
                
                <div className={styles.faqGrid}>
                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>De onde vêm os dados?</h4>
                    <p className={styles.faqAnswer}>
                      Dos sistemas acadêmicos e financeiros da instituição, consolidados na plataforma.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>Quando é executado novamente?</h4>
                    <p className={styles.faqAnswer}>
                      O algoritmo é executado toda semana automaticamente.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>Por que só aparecem alunos em dia?</h4>
                    <p className={styles.faqAnswer}>
                      O objetivo é prever quem ainda está em dia, mas pode atrasar. Alunos já inadimplentes 
                      podem ser consultados na tela de <a href="/app/alunos-inadimplentes" className={styles.infoLink}>Alunos Inadimplentes</a>.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>Como interpretar as faixas de risco?</h4>
                    <p className={styles.faqAnswer}>
                      <strong>Baixo risco:</strong> Acompanhamento normal.<br />
                      <strong>Médio risco:</strong> Atenção redobrada.<br />
                      <strong>Alto risco:</strong> Contato proativo imediato.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>O que significam os "motivos" que aparecem?</h4>
                    <p className={styles.faqAnswer}>
                      São os principais fatores que indicam risco, como frequência baixa, notas ruins ou atraso 
                      em pagamentos anteriores.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>Posso exportar?</h4>
                    <p className={styles.faqAnswer}>
                      Sim, é possível exportar a lista em formato CSV.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.fullScreenFooter}>
              <button className={styles.fullScreenCloseButtonFooter} onClick={handleCloseInfoModal}>
                Voltar para a Análise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para exibir motivos detalhados de inadimplência */}
      {modalOpen && selectedStudentFactors && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                Motivos de Inadimplência - Aluno {selectedStudentFactors.studentId}
              </h3>
              <button className={styles.modalCloseButton} onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {selectedStudentFactors.factors.length > 0 ? (
                <div className={styles.factorsList}>
                  {selectedStudentFactors.factors.map((factor, index) => (
                    <div key={index} className={styles.factorItem}>
                      <div className={styles.factorHeader}>
                        <span className={styles.factorName}>{factor.friendlyName}</span>
                        <span className={styles.factorContribution}>
                          Impacto: {factor.formattedContribution || '-'}
                        </span>
                      </div>
                      <div className={styles.factorDescription}>
                        {factor.description}
                      </div>
                      <div className={styles.factorValuesRow}>
                        <div className={styles.factorValueItem}>
                          <span className={styles.factorValueLabel}>Aluno</span>
                          <strong className={factor.isMonetary ? styles.monetaryValue : factor.isPercentage ? styles.percentageValue : ''}>{factor.value}</strong>
                        </div>
                        {factor.formattedAvg && (
                          <div className={styles.factorValueItem}>
                            <span className={styles.factorValueLabel}>Média da instituição</span>
                            <strong>{factor.formattedAvg}</strong>
                          </div>
                        )}
                      </div>
                      {factor.comparisonText && (
                        <div className={styles.factorComparison}>
                          {factor.comparisonText}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.noFactors}>Nenhum fator de inadimplência disponível.</p>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCloseButtonFooter} onClick={handleCloseModal}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prediction;